import { useCallback, useMemo } from 'react';
import { getAllCompositeSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { FitType } from '@goldcast/player-config/src/types/asset-types';
import { CLIP_UPDATE_TYPE } from '../../ClipNewTimeline/clipUpdateDataUtils/constants';
import { updateClipData } from '../../ClipNewTimeline/clipUpdateDataUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useDialog from '@/components/organisms/useDialog';
import { hasMoreThanOneCompositeSegment } from '@/stores/clip';

export const useClipFitFill = ({ fitOption }: { fitOption: FitType }) => {
  const { clipId, clipData } = useClipsContext();
  const { isOpen, openDialog, closeDialog } = useDialog();
  const moreThanOneCompositeSegment = hasMoreThanOneCompositeSegment(clipData);
  const clipFitMode = useMemo(() => {
    if (moreThanOneCompositeSegment) {
      const allScenesFitModes = getAllCompositeSegments(clipData).flatMap(compositeSegment =>
        compositeSegment.scenes.map(scene => scene.details.fitMode)
      );
      const fitModes = new Set(allScenesFitModes);
      if (fitModes.size > 1) {
        return clipData.asset_metadata.fit;
      }
      return allScenesFitModes[0];
    }
    const scenes = clipData.asset_metadata.scenes;
    if (scenes && scenes.length > 1) {
      return scenes[0].details.fitMode;
    }
    return clipData.asset_metadata.fit;
  }, [clipData, moreThanOneCompositeSegment]);

  const hasMultipleScenes = useMemo(() => {
    if (moreThanOneCompositeSegment) {
      const allScenesFitModes = getAllCompositeSegments(clipData).flatMap(compositeSegment =>
        compositeSegment.scenes.map(scene => scene.details.fitMode)
      );
      const fitModes = new Set(allScenesFitModes);
      return fitModes.size > 1;
    }
    const scenes = clipData.asset_metadata.scenes;
    if (scenes && scenes.length > 1) {
      return true;
    }
    return false;
  }, [clipData, moreThanOneCompositeSegment]);

  const changeFitMode = useCallback(() => {
    if (hasMultipleScenes) {
      openDialog();
      return;
    }

    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_FIT_MODE,
      fitMode: fitOption
    });
  }, [clipId, fitOption, hasMultipleScenes, openDialog]);

  const handleConfirm = useCallback(() => {
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_FIT_MODE,
      fitMode: fitOption
    });
    closeDialog();
  }, [clipId, fitOption, closeDialog]);

  const handleCancel = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  return {
    hasMultipleScenes,
    clipFitMode,
    isOpen,
    openDialog,
    closeDialog,
    changeFitMode,
    handleConfirm,
    handleCancel
  };
};
